import Papa from 'papaparse'
import { useState } from 'react'

interface UseFileParserProps {
  file: File | null
  config?: {
    header?: boolean
    skipEmptyLines?: boolean
    transform?: (value: string, field: string) => any
    complete?: (results: any) => void
    error?: (error: any) => void
  }
}

interface UseFileParserReturn {
  data: any[]
  error: string | null
  isLoading: boolean
  parse: () => void
}

export const useFileParser = ({
  file,
  config = {
    header: true,
    skipEmptyLines: true,
    transform: (value, field) => {
      if (field.toLowerCase().includes('date')) {
        return new Date(value)
      }
      if (
        field.toLowerCase().includes('amount') ||
        field.toLowerCase().includes('rate') ||
        field.toLowerCase().includes('value')
      ) {
        return parseFloat(value)
      }
      return value
    },
  },
}: UseFileParserProps): UseFileParserReturn => {
  const [data, setData] = useState<any[]>([])
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const parse = () => {
    if (!file) return

    setIsLoading(true)
    setError(null)

    const fileExtension = file.name.split('.').pop()?.toLowerCase()

    switch (fileExtension) {
      case 'csv':
        Papa.parse(file, {
          ...config,
          complete: (results) => {
            setData(results.data)
            config.complete?.(results)
            setIsLoading(false)
          },
          error: (err) => {
            setError(`CSV parsing error: ${err.message}`)
            config.error?.(err)
            setIsLoading(false)
          },
        })
        break

      case 'flw':
        // Add your FLW parsing logic here
        // This is just a placeholder - you'll need to implement the actual FLW parsing
        const reader = new FileReader()
        reader.onload = (e) => {
          try {
            const text = e.target?.result as string
            // Implement your FLW parsing logic
            const parsedData = parseFLW(text)
            setData(parsedData)
            config.complete?.(parsedData)
          } catch (err: any) {
            setError(`FLW parsing error: ${err.message}`)
            config.error?.(err)
          } finally {
            setIsLoading(false)
          }
        }
        reader.onerror = (err: any) => {
          setError(`FLW parsing error: ${err.message}`)
          config.error?.(err)
          setIsLoading(false)
        }
        reader.readAsText(file)
        break

      case 'rat':
        // Add your RAT parsing logic here
        // This is just a placeholder - you'll need to implement the actual RAT parsing
        const reader2 = new FileReader()
        reader2.onload = (e) => {
          try {
            const text = e.target?.result as string
            // Implement your RAT parsing logic
            const parsedData = parseRAT(text)
            setData(parsedData)
            config.complete?.(parsedData)
          } catch (err: any) {
            setError(`RAT parsing error: ${err.message}`)
            config.error?.(err)
          } finally {
            setIsLoading(false)
          }
        }
        reader2.onerror = (err: any) => {
          setError(`RAT parsing error: ${err.message}`)
          config.error?.(err)
          setIsLoading(false)
        }
        reader2.readAsText(file)
        break

      default:
        setError(`Unsupported file format: ${fileExtension}`)
        setIsLoading(false)
    }
  }

  return { data, error, isLoading, parse }
}

// Helper functions for parsing specific file formats
const parseFLW = (text: string): any[] => {
  return parseTwoColData(text)
}

const parseRAT = (text: string): any[] => {
  return parseTwoColData(text)
}

const parseTwoColData = (text: string): any[] => {
  const lines = text.split('\n')
  const parsedData = lines.map((line) => {
    const row = line
      .split(/[\s,]+/)
      .map((str) => str.trim())
      ?.filter((str) => str !== '')
    if (row.length === 0) {
      return null
    }
    return {
      column1: row[0],
      column2: Number(row[1]),
    }
  })
  return parsedData.filter((d) => d !== null)
}
